import {AbstractComponent} from './abstract-component';
import mitt, {Emitter} from 'mitt';

type PaginationSettings = {
  start: number,
  rows: number,
  numFound: number,
  maxLinks: number
};

type PaginationPage = {
  page: number,
  start: number,
  rows: number
};

type PaginationEvent = {
  browse: PaginationSettings
};


/**
 * Pagination for a list of items.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export class Pagination extends AbstractComponent<PaginationSettings> {
  public readonly emitter: Emitter<PaginationEvent> = mitt<PaginationEvent>();
  protected _container: HTMLDivElement;

  constructor(container: HTMLDivElement, settings: Partial<PaginationSettings> = {}) {
    super();
    const defaultSettings: PaginationSettings = {
      start: 0,
      rows: 12,
      numFound: 0,
      maxLinks: 5
    };
    this.settings = Object.assign(
      {},
      defaultSettings,
      settings
    );
    this._container = container;
    this.update();
  }

  protected get container(): HTMLElement {
    return this._container;
  }

  /**
   * Updates the pagination.
   */
  update(): void {
    // If no results are present, hide pagination
    if (this.settings.numFound === 0) {
      this.container.classList.add('d-none');
      return;
    }

    // Calculate new pagination settings
    const paginationParameters = this.calculatePaginationParameters();
    if (paginationParameters.totalPages === 1) {
      this.container.classList.add('d-none');
      return;
    }

    this.container.classList.remove('d-none');

    // Render handlebars template
    const template = require('../handlebars/templates/pagination.handlebars');
    this.container.innerHTML = template(paginationParameters);

    // Initialize new pagination buttons
    this.initializeBrowser();
  }

  /**
   * Browser to a new page.
   *
   * @param {number} start New start index
   * @param {number} [rows] New number of rows to list (defaults to the previously used setting/default setting)
   */
  browse(start: number, rows: number = 0): void {
    this.settings.start = start;
    if (rows > 0) {
      this.settings.rows = rows;
    }
    this.update();
    this.emitter.emit('browse', this.settings);
  }

  /**
   * Updates the number of items being paginated.
   *
   * @param {number} numFound Number of items
   */
  updateNumFound(numFound: number): void {
    this.settings.numFound = numFound;
    this.update();
  }

  /**
   * Resets the pagination to the first page.
   *
   * @param {number} [numFound] Optionally force a new number of items to be paginated (defaults to 0)
   */
  reset(numFound: number = 0): void {
    this.settings.start = 0;
    if (numFound !== null) {
      this.settings.numFound = numFound;
    }
    this.update();
  }

  /**
   * Initializes the pagination buttons.
   */
  private initializeBrowser(): void {
    const links = this.container.querySelectorAll<HTMLAnchorElement>('[data-page]');
    for (const link of links) {
      if (!link.dataset.start || !link.dataset.rows) {
        continue;
      }
      const start = parseInt(link.dataset.start);
      const rows = parseInt(link.dataset.rows);
      if (isNaN(start) || isNaN(rows)) {
        continue;
      }
      link.addEventListener('click', e => {
        e.preventDefault();
        this.browse(start, rows);
      });
    }
  }

  /**
   * Calculates new pagination parameters. Current pagination settings are used as a base for calculations.
   *
   * @returns { [key: string]: any } Pagination parameters
   */
  private calculatePaginationParameters(): { [key: string]: any } {
    const settings = this.settings;

    // The number of links needs to be uneven and greater than three, for the pagination to work in all variants
    if (settings.maxLinks < 3) {
      settings.maxLinks = 3;
    }
    if (settings.maxLinks % 2 === 0) {
      settings.maxLinks++;
    }

    // Calculate current page
    let page = 1;
    if (settings.start >= settings.rows) {
      page = (settings.start / settings.rows) + 1;
    }

    // Calculate the total amount of pages
    const totalPages: number = Math.ceil(settings.numFound / settings.rows);

    // Calculate start and end indices
    let first: PaginationPage | null = null;
    let last: PaginationPage | null = null;
    const half: number = settings.maxLinks - Math.ceil(settings.maxLinks / 2);

    let start: number = page - half;
    if (start <= 0) {
      start = 1;
    }

    let end: number = start + settings.maxLinks;
    if (end > totalPages) {
      start -= end - totalPages - 1;
      end = totalPages + 1; // +1 to account for current page
    }

    if (settings.maxLinks >= totalPages) {
      start = 1;
      end = totalPages + 1;
    }

    // Create the initial page list
    const pages: PaginationPage[] = [];

    for (let i = start; i < end; i++) {
      pages.push({
        page: i,
        start: settings.rows * (i - 1),
        rows: settings.rows
      });
    }

    if (totalPages > settings.maxLinks) {
      // The very first page needs to be shown, as the current page has been moved ahead far enough
      if (page !== 1 && page > half + 1) {
        first = {
          page: 1,
          start: 0,
          rows: settings.rows
        };
      }

      // The very last page needs to be shown, as the current page has been moved behind far enough
      if (page !== totalPages && page < totalPages - half) {
        last = {
          page: totalPages,
          start: settings.rows * (totalPages - 1),
          rows: settings.rows
        };
      }
    }

    // Calculate previous and next parameters
    let previous: PaginationPage | null = null;
    let next: PaginationPage | null = null;

    if (page > 1) {
      previous = {
        page: page - 1,
        start: settings.rows * (page - 2),
        rows: settings.rows
      };
    }

    if (page !== totalPages) {
      next = {
        page: page + 1,
        start: settings.rows * page,
        rows: settings.rows
      };
    }

    return {
      itemsPerPage: settings.rows,
      numFound: settings.numFound,
      totalPages: totalPages,
      selectedPage: page,
      pages: pages,
      previous: previous,
      next: next,
      first: first,
      last: last
    };
  }
}
